import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePositionStore } from '../stores/positionStore';
import { usePaperPositionStore } from '../stores/paperPositionStore';
import api from '../lib/api';
import socketManager from '../lib/socket';
import {
  LayoutDashboard,
  Users,
  Target,
  Briefcase,
  BarChart3,
  Settings,
  LogOut,
  Wallet,
  Menu,
  X,
  Crown,
  Lock,
  Zap,
  Gift,
  BookOpen,
  HelpCircle,
  Share2,
  Flame
} from 'lucide-react';
import { useEffect, useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Tutorial', href: '/tutorial', icon: BookOpen },
  { name: 'Find Traders', href: '/traders', icon: Users },
  { name: 'Copy Configs', href: '/copy-configs', icon: Target },
  { name: 'Positions', href: '/positions', icon: Briefcase },
  { name: 'Markets', href: '/markets', icon: BarChart3 },
  { name: 'Whale Vault', href: '/whale-vault', icon: Crown },
  { name: 'Arbitrage', href: '/arbitrage', icon: Zap },
  { name: 'Degen Mode', href: '/degen', icon: Flame },
  { name: 'Pricing', href: '/pricing', icon: Crown },
  { name: 'Referrals', href: '/referrals', icon: Share2 },
  { name: 'Help', href: '/help', icon: HelpCircle },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// BetMimic Logo Component
function BetMimicLogo({ size = 'default' }) {
  const sizes = {
    small: 'w-8 h-8',
    default: 'w-10 h-10',
    large: 'w-12 h-12'
  };
  
  return (
    <div className={`${sizes[size]} relative`}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl rotate-6 opacity-60 blur-sm" />
      <div className="relative w-full h-full bg-dark-900/20 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25 overflow-hidden">
        <img
          src="/BETMIMICLOGO.jpg"
          alt="BetMimic"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { updatePosition, addTrade, fetchPositions, fetchSummary, fetchTrades } = usePositionStore();
  const {
    updatePosition: updatePaperPosition,
    addTrade: addPaperTrade,
    fetchPositions: fetchPaperPositions,
    fetchSummary: fetchPaperSummary,
    fetchTrades: fetchPaperTrades
  } = usePaperPositionStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);

  useEffect(() => {
    let canceled = false;

    const fetchSubscription = async () => {
      try {
        const res = await api.get('/payments/subscription');
        if (!canceled) {
          setSubscriptionTier(res.data?.effectiveTier || res.data?.tier || 'free');
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        if (!canceled) {
          setSubscriptionTier('free');
        }
      }
    };

    fetchSubscription();
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?._id) return;

    let canceled = false;

    const hydrate = async () => {
      try {
        const snap = usePositionStore.getState();
        const hasPositions = Array.isArray(snap?.positions) && snap.positions.length > 0;
        const hasTrades = Array.isArray(snap?.trades) && snap.trades.length > 0;
        const hasSummary = !!snap?.summary;

        if (!hasPositions) {
          await fetchPositions('open');
        }
        if (!hasTrades) {
          await fetchTrades({ limit: 50 });
        }
        if (!hasSummary) {
          await fetchSummary();
        }
      } catch (_) {
      }

      try {
        const snap = usePaperPositionStore.getState();
        const hasPositions = Array.isArray(snap?.positions) && snap.positions.length > 0;
        const hasTrades = Array.isArray(snap?.trades) && snap.trades.length > 0;
        const hasSummary = !!snap?.summary;

        if (!hasPositions) {
          await fetchPaperPositions('open');
        }
        if (!hasTrades) {
          await fetchPaperTrades({ limit: 50 });
        }
        if (!hasSummary) {
          await fetchPaperSummary();
        }
      } catch (_) {
      }
    };

    if (!canceled) {
      hydrate();
    }

    socketManager.subscribeToPositions(user._id);

    const onPositionUpdated = (position) => {
      if (!position) return;
      updatePosition(position);
    };

    const onTradeExecuted = (trade) => {
      if (!trade) return;
      addTrade(trade);
    };

    const onTradeFailed = (trade) => {
      if (!trade) return;
      addTrade(trade);
    };

    const onTradePending = (trade) => {
      if (!trade) return;
      addTrade(trade);
    };

    const onStopLossTriggered = ({ position }) => {
      if (!position) return;
      updatePosition(position);
    };

    const onTakeProfitTriggered = ({ position }) => {
      if (!position) return;
      updatePosition(position);
    };

    const onPaperPositionUpdated = (position) => {
      if (!position) return;
      updatePaperPosition(position);
    };

    const onPaperTradeExecuted = (trade) => {
      if (!trade) return;
      addPaperTrade(trade);
    };

    const onPaperStopLossTriggered = ({ position, trade }) => {
      if (position) updatePaperPosition(position);
      if (trade) addPaperTrade(trade);
    };

    const onPaperTakeProfitTriggered = ({ position, trade }) => {
      if (position) updatePaperPosition(position);
      if (trade) addPaperTrade(trade);
    };

    socketManager.on('positionUpdated', onPositionUpdated);
    socketManager.on('tradeExecuted', onTradeExecuted);
    socketManager.on('tradeFailed', onTradeFailed);
    socketManager.on('tradePending', onTradePending);
    socketManager.on('stopLossTriggered', onStopLossTriggered);
    socketManager.on('takeProfitTriggered', onTakeProfitTriggered);

    socketManager.on('paperPositionUpdated', onPaperPositionUpdated);
    socketManager.on('paperTradeExecuted', onPaperTradeExecuted);
    socketManager.on('paperStopLossTriggered', onPaperStopLossTriggered);
    socketManager.on('paperTakeProfitTriggered', onPaperTakeProfitTriggered);

    return () => {
      canceled = true;
      socketManager.off('positionUpdated', onPositionUpdated);
      socketManager.off('tradeExecuted', onTradeExecuted);
      socketManager.off('tradeFailed', onTradeFailed);
      socketManager.off('tradePending', onTradePending);
      socketManager.off('stopLossTriggered', onStopLossTriggered);
      socketManager.off('takeProfitTriggered', onTakeProfitTriggered);

      socketManager.off('paperPositionUpdated', onPaperPositionUpdated);
      socketManager.off('paperTradeExecuted', onPaperTradeExecuted);
      socketManager.off('paperStopLossTriggered', onPaperStopLossTriggered);
      socketManager.off('paperTakeProfitTriggered', onPaperTakeProfitTriggered);
    };
  }, [user?._id, updatePosition, addTrade, updatePaperPosition, addPaperTrade, fetchPositions, fetchSummary, fetchTrades, fetchPaperPositions, fetchPaperSummary, fetchPaperTrades]);

  const computedNavigation = subscriptionTier && subscriptionTier !== 'free'
    ? [
        ...navigation.slice(0, 6),
        { name: 'Paper Trading', href: '/paper', icon: Briefcase },
        ...navigation.slice(6)
      ]
    : navigation;

  const finalNavigation = computedNavigation
    .filter((item) => {
      // Hide Arbitrage from non-whale users
      if (item.href === '/arbitrage' && subscriptionTier !== 'whale') {
        return false;
      }
      // Hide Whale Vault from non-whale users completely
      if (item.href === '/whale-vault' && subscriptionTier !== 'whale') {
        return false;
      }
      return true;
    });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-dark-800/95 backdrop-blur-xl border-r border-dark-700/50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-dark-700/50">
            <Link to="/" className="flex items-center gap-3 group">
              <BetMimicLogo />
              <div>
                <span className="font-bold text-xl text-gradient">BetMimic</span>
                <p className="text-[10px] text-dark-500 font-medium tracking-wider uppercase">Copy Trading</p>
              </div>
            </Link>
            <button
              className="lg:hidden p-2 hover:bg-dark-700/50 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
            {finalNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-600/20 to-accent-600/10 text-white border border-primary-500/20'
                      : 'text-dark-400 hover:bg-dark-700/50 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className={`p-2 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-primary-600/20 text-primary-400' 
                      : 'bg-dark-700/50 text-dark-400 group-hover:text-primary-400 group-hover:bg-primary-600/10'
                  }`}>
                    <item.icon className="w-4 h-4" />
                  </div>
                  <span className="font-medium">{item.name}</span>
                  {isActive && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                  )}
                </Link>
              );
            })}
            
            {/* Promo Code Section */}
            <div className="pt-4 mt-4 border-t border-dark-700/50">
              <Link
                to="/pricing#promo"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-600/10 to-accent-600/10 border border-primary-500/20 text-primary-400 hover:from-primary-600/20 hover:to-accent-600/20 transition-all"
              >
                <div className="p-2 rounded-lg bg-primary-600/20">
                  <Gift className="w-4 h-4" />
                </div>
                <span className="font-medium">Redeem Code</span>
                <Zap className="w-4 h-4 ml-auto text-accent-400" />
              </Link>
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-dark-700/50">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-dark-700/30 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                <span className="text-sm font-bold text-white">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{user?.email}</p>
                <p className="text-xs flex items-center gap-1.5 mt-0.5">
                  <Wallet className="w-3 h-3" />
                  {user?.wallet?.isConnected ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Connected
                    </span>
                  ) : (
                    <span className="text-amber-400">Not connected</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm text-dark-400 hover:text-white hover:bg-dark-700/50 rounded-xl transition-all duration-200 border border-transparent hover:border-dark-600/50"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-dark-900/80 backdrop-blur-xl border-b border-dark-700/50">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button
              className="lg:hidden p-2.5 hover:bg-dark-700/50 rounded-xl transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 ml-auto">
              {user?.wallet?.isConnected && (
                <div className="hidden sm:flex items-center gap-2.5 px-4 py-2 bg-dark-800/60 backdrop-blur-sm rounded-xl border border-dark-700/50">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50" />
                  <span className="text-sm font-mono text-dark-300">
                    {user.wallet.address?.slice(0, 6)}...{user.wallet.address?.slice(-4)}
                  </span>
                </div>
              )}
              
              {/* Quick Actions */}
              <Link
                to="/traders"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 rounded-xl text-white text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all"
              >
                <Zap className="w-4 h-4" />
                Find Traders
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
